import { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from "axios"
import * as Sentry from "@sentry/react"

declare global {
	interface Window {
		sentry?: any
	}
}

function generateCorrelationId(): string {
	return `req-${Math.random().toString(36).substring(2, 10)}`
}

/**
 * Apply Sentry interceptors to an Axios instance for request/response tracking.
 * Generates correlation IDs and propagates via X-Correlation-ID header
 * Adds breadcrumbs for all API requests
 */
export const applySentryInterceptor = (axios: AxiosInstance) => {
	// request capture generate correlation ID, add breadcrumb, set header
	axios.interceptors.request.use(
		(config: AxiosRequestConfig) => {
			const correlationId = generateCorrelationId()

			// correlation ID header
			config.headers["X-Correlation-ID"] = correlationId

			// Store on config for use in response interceptor
			;(config as any)._correlationId = correlationId

			// tag Sentry scope with correlation ID
			Sentry.setTag("correlation_id", correlationId)

			// Add breadcrumb for request
			Sentry.addBreadcrumb({
				category: "http",
				message: `${config.method?.toUpperCase()} ${config.url}`,
				level: "info",
				data: {
					method: config.method,
					url: config.url,
					correlation_id: correlationId,
				},
			})

			return config
		},
		(error) => {
			Sentry.captureException(error)
			return Promise.reject(error)
		}
	)

	// Response capture errors with correlation ID
	axios.interceptors.response.use(
		(response: AxiosResponse) => {
			// add breadcrumb
			const correlationId = (response.config as any)._correlationId
			Sentry.addBreadcrumb({
				category: "http",
				message: `${response.status} ${response.config.method?.toUpperCase()} ${
					response.config.url
				}`,
				level: "info",
				data: {
					status: response.status,
					correlation_id: correlationId,
				},
			})
			return response
		},
		(error: AxiosError) => {
			const correlationId = (error.config as any)?._correlationId

			if (error.response) {
				// error breadcrumb
				Sentry.addBreadcrumb({
					category: "http.error",
					message: `${
						error.response.status
					} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
					level: "error",
					data: {
						status: error.response.status,
						correlation_id: correlationId,
					},
				})

				// raise to Sentry with correlation_id and detailed context
				Sentry.captureException(error, {
					tags: {
						correlation_id: correlationId,
						api_url: error.config?.url,
						api_method: error.config?.method,
						http_status: error.response.status,
					},
					contexts: {
						response: {
							status: error.response.status,
							data: error.response.data,
						},
					},
				})
			} else {
				// Network error or request setup error
				Sentry.captureException(error, {
					tags: {
						correlation_id: correlationId,
						api_url: error.config?.url,
						api_method: error.config?.method,
					},
				})
			}

			return Promise.reject(error)
		}
	)
}
